import { useMutation } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { deleteDocument } from '~extension/web2print/controller/api/document';
import { queries } from '~extension/web2print/controller/api/Queries';
import { makeDocumenDelete } from '~extension/web2print/model/DocumentDeleteFactory';
import { DeleteConfirmModal } from '~extension/web2print/view/Delete/DeleteConfirmModal';
import useDelayFn from '~hooks/useDelayFn';

export function DeleteModalController({
  documentId,
  clientId,
  onClose,
  jobStatus,
}: {
  documentId: number;
  clientId: number;
  onClose: () => void;
  jobStatus: string;
}) {
  const delayedOnClose = useDelayFn(onClose, 500);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false);
  const [documentIsOpen, setDocumentIsOpen] = useState<boolean>(undefined);

  const queryClient = useQueryClient();

  // open delete confirm modal if IDs are given
  useEffect(() => {
    setDeleteConfirmed(false);
    setIsLoading(false);
    setDocumentIsOpen(undefined);
    setIsOpen(Boolean(documentId) && Boolean(clientId));
  }, [documentId, clientId]);

  // Handler: close dialog, but delay the closing to have smoother transitions
  const handleOnClose = useCallback(() => {
    setIsOpen(false);
    delayedOnClose();
  }, [delayedOnClose]);

  // trigger delete document
  const { mutate } = useMutation({
    mutationFn: async () => makeDocumenDelete(await deleteDocument(documentId, clientId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ ...queries.web2print.jobList(jobStatus) });
      handleOnClose();
    },
  });

  // check if document is closed
  const { data: queryData } = useQuery({
    ...queries.web2print.documentIsOpen(documentId, clientId),
    enabled: Boolean(isOpen) && Boolean(deleteConfirmed) && (documentIsOpen === undefined || Boolean(documentIsOpen)),
    refetchInterval: 10 * 1000,
  });

  useEffect(() => {
    if (queryData === undefined) return;
    setDocumentIsOpen(queryData);
    if (queryData === false) mutate();
  }, [mutate, queryData]);

  const onDelete = () => {
    setDeleteConfirmed(true);
    setIsLoading(true);
  };

  return <DeleteConfirmModal isOpen={isOpen} isLoading={isLoading} onClose={handleOnClose} onDelete={onDelete} />;
}
